/**
 * DocumentsTab - Read-Only Document Browser (TASK-282, TASK-361)
 *
 * Displays all client documents from MinIO storage.
 * Upload functionality has been moved to domain-specific uploaders:
 * - KYC Form → DocumentUploadStep (passport, ID, bank statements)
 * - Portfolio Tab → PortfolioFileUploader (Excel/CSV/PDF statements)
 * - Risk Profile → Signed PDF upload
 * - AML/Compliance → DocumentAnalysisTab
 *
 * TASK-361: Added secure upload link management for requesting documents from clients
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FileText, AlertCircle, Link2, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { uploadService } from '../../../services/uploadService';
import PDFPreviewModal from '../../PDFPreviewModal';
import DataPreviewModal from '../../DataPreviewModal';
import { ExtractionPreviewModal } from '../../documents/ExtractionPreviewModal';
import { MinIODocumentsSection, GenerateUploadLinkModal } from '../../documents';
import DocumentCard from './DocumentCard';
import DocumentFilters from './DocumentFilters';
import { KYCExtractionReview } from '../../meeting/PostMeetingHub/KYCExtractionReview';
import { getMockDocuments, hasMockDocuments } from '../../../services/mock/mockDocuments';
import { secureUploadLinkService } from '../../../services/secureUploadLinkService';
import { SecureUploadLink, getUploadLinkStatusColor, getUploadLinkStatusLabel } from '../../../types/documentCompliance';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

interface Upload {
  upload_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  template_type: string;
  upload_status: string;
  total_rows: number | null;
  processed_rows: number | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface Filters {
  type: 'all' | 'pdf' | 'excel' | 'csv';
  search: string;
}

interface PendingReview {
  staging_id: string;
  upload_id?: string;
  filename?: string;
  target_form_type: string;
  extraction_confidence: number;
  created_at: string;
}

interface DocumentsTabProps {
  clientId: string;
  clientName?: string;
  clientEmail?: string;
  onNavigateToCompliance?: () => void; // TASK-232: Navigate to compliance tab after prefill
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  clientId,
  clientName,
  clientEmail,
  onNavigateToCompliance,
}) => {
  const [documents, setDocuments] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>({ type: 'all', search: '' });

  // TASK-282: Track MinIO document count
  const [minioDocumentCount, setMinioDocumentCount] = useState(0);

  // Preview modal state
  const [previewUploadId, setPreviewUploadId] = useState<string | null>(null);
  const [previewStagingId, setPreviewStagingId] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'pdf' | 'data' | 'extraction' | null>(null);

  // Pending reviews state (TASK-191)
  const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
  const [showKYCReview, setShowKYCReview] = useState(false);
  const [reviewingStagingId, setReviewingStagingId] = useState<string | null>(null);

  // TASK-361: Secure upload link state
  const [uploadLinks, setUploadLinks] = useState<SecureUploadLink[]>([]);
  const [uploadLinksExpanded, setUploadLinksExpanded] = useState(false);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);

  // Fetch documents on mount and when clientId changes
  useEffect(() => {
    fetchDocuments();
    fetchPendingReviews();
    fetchUploadLinks();
  }, [clientId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // TASK-210: Merge API documents with mock documents for seed clients
      let apiDocuments: Upload[] = [];

      // Try to fetch from API
      try {
        apiDocuments = await uploadService.getAllUploads(clientId);
        console.log(`Loaded ${apiDocuments.length} documents from API for client ${clientId}`);
      } catch (error) {
        console.error('API documents fetch failed, continuing with mock data only:', error);
        toast.error('Failed to load documents from server');
      }

      // Check if this is a seed client and add mock documents
      if (hasMockDocuments(clientId)) {
        const mockDocuments = getMockDocuments(clientId);

        // Merge: Add mock documents that don't exist in API response
        const apiDocumentIds = new Set(apiDocuments.map(d => d.upload_id));
        const uniqueMockDocuments = mockDocuments.filter(d => !apiDocumentIds.has(d.upload_id));

        const allDocuments = [...apiDocuments, ...uniqueMockDocuments as Upload[]];

        setDocuments(allDocuments);
        console.log(`Loaded ${allDocuments.length} total documents (${apiDocuments.length} from API, ${uniqueMockDocuments.length} from seed data) for client ${clientId}`);
      } else {
        // Not a seed client, just use API documents
        setDocuments(apiDocuments);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingReviews = async () => {
    try {
      const response = await uploadService.getPendingReviews(clientId);
      setPendingReviews(response.reviews || []);
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      toast.error('Failed to load pending reviews');
    }
  };

  // TASK-361: Fetch upload links for this client
  const fetchUploadLinks = async () => {
    try {
      const response = await secureUploadLinkService.getClientLinks(clientId);
      setUploadLinks(response.links || []);
      // Auto-expand if there are active links
      if (response.links?.some((l: SecureUploadLink) => l.status === 'active')) {
        setUploadLinksExpanded(true);
      }
    } catch (error) {
      console.error('Error fetching upload links:', error);
      toast.error('Failed to load upload links');
    }
  };

  // TASK-361: Handle link creation success
  const handleLinkCreated = (_portalUrl: string) => {
    toast.success('Upload link created successfully');
    setShowCreateLinkModal(false);
    fetchUploadLinks();
  };

  // Filter documents based on type and search query
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      // Type filter
      const matchesType = (() => {
        if (filters.type === 'all') return true;
        if (filters.type === 'pdf') return doc.file_type.includes('pdf');
        if (filters.type === 'excel') return doc.file_type.includes('excel') || doc.file_type.includes('spreadsheet');
        if (filters.type === 'csv') return doc.file_type.includes('csv');
        return true;
      })();

      // Search filter
      const matchesSearch = doc.filename.toLowerCase().includes(filters.search.toLowerCase());

      return matchesType && matchesSearch;
    });
  }, [documents, filters]);

  // Handle view document
  const handleView = (document: Upload) => {
    const isPdf = document.file_type.includes('pdf');

    setPreviewUploadId(document.upload_id);
    setPreviewType(isPdf ? 'pdf' : 'data');
  };

  // Handle preview modal close
  const handleClosePreview = () => {
    setPreviewUploadId(null);
    setPreviewStagingId(null);
    setPreviewType(null);
  };

  // Handle review button click - open unified extraction preview modal
  const handleReviewDocument = (review: PendingReview) => {
    console.log('📋 Opening review for:', review);

    // Use upload_id if available, otherwise fall back to staging_id
    const uploadId = review.upload_id || review.staging_id;

    // Store staging_id for approval endpoint
    setPreviewStagingId(review.staging_id);

    // Open unified extraction preview modal
    setPreviewUploadId(uploadId);
    setPreviewType('extraction');

    console.log('🎯 Opening extraction preview modal:', { uploadId, stagingId: review.staging_id });
  };

  // Handle KYC review approved
  const handleKYCApproved = () => {
    toast.success('KYC data approved and saved successfully');
    setShowKYCReview(false);
    setReviewingStagingId(null);
    // Refresh both documents and pending reviews
    fetchDocuments();
    fetchPendingReviews();
  };

  // Handle KYC review close
  const handleKYCReviewClose = () => {
    setShowKYCReview(false);
    setReviewingStagingId(null);
  };

  // Total documents (legacy + MinIO)
  const totalDocuments = documents.length + minioDocumentCount;

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-5 h-5 bg-gray-200 rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
              <div className="flex gap-2">
                <div className="w-8 h-8 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state - TASK-282: Guide users to domain-specific uploaders
  if (totalDocuments === 0 && !loading && pendingReviews.length === 0) {
    return (
      <div className="space-y-6">
        {/* TASK-361: Request Documents Section - always visible */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => setUploadLinksExpanded(!uploadLinksExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-[#002B45]" />
              <span className="font-medium text-gray-900">Request Documents from Client</span>
              {uploadLinks.filter((l) => l.status === 'active').length > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  {uploadLinks.filter((l) => l.status === 'active').length} active
                </span>
              )}
            </div>
            {uploadLinksExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {uploadLinksExpanded && (
            <div className="border-t border-gray-100 p-4 space-y-4">
              <Button
                onClick={() => setShowCreateLinkModal(true)}
                className="flex items-center gap-2"
              >
                <Plus size={16} />
                Generate Secure Upload Link
              </Button>

              {uploadLinks.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Recent Upload Links</p>
                  {uploadLinks.slice(0, 5).map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUploadLinkStatusColor(link.status)}`}>
                          {getUploadLinkStatusLabel(link.status)}
                        </span>
                        <div className="text-sm">
                          <span className="text-gray-600">
                            {link.uploads_used}/{link.max_uploads} uploads
                          </span>
                          <span className="text-gray-400 mx-2">&bull;</span>
                          <span className="text-gray-500">
                            Expires {new Date(link.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {link.status === 'active' && link.portal_url && (
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(link.portal_url!);
                              toast.success('Link copied to clipboard');
                            } catch {
                              toast.error('Failed to copy link');
                            }
                          }}
                          className="text-sm text-[#002B45] hover:underline"
                        >
                          Copy Link
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {uploadLinks.length === 0 && (
                <p className="text-sm text-gray-500">
                  No upload links created yet. Generate a secure link to request documents from this client.
                </p>
              )}
            </div>
          )}
        </div>

        {/* TASK-282: MinIO Documents Section (will show empty state with guidance) */}
        <MinIODocumentsSection
          clientId={clientId}
          onDocumentCountChange={setMinioDocumentCount}
        />

        {/* Additional guidance */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-900 mb-2">
                How to add documents
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                Documents are automatically saved when uploaded through specialized workflows:
              </p>
              <ul className="text-sm text-blue-700 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-lg">🪪</span>
                  <span><strong>KYC Tab</strong> - Upload passports, ID cards, and KYC forms</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-lg">📊</span>
                  <span><strong>Portfolio Tab</strong> - Import portfolio statements (Excel, CSV, PDF)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-lg">⚠️</span>
                  <span><strong>Risk Profile</strong> - Upload signed risk questionnaires</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-lg">🔍</span>
                  <span><strong>Compliance Tab</strong> - AML and compliance document analysis</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* TASK-361: Generate Upload Link Modal */}
        <GenerateUploadLinkModal
          isOpen={showCreateLinkModal}
          onClose={() => setShowCreateLinkModal(false)}
          clientId={clientId}
          clientName={clientName}
          clientEmail={clientEmail}
          onSuccess={handleLinkCreated}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Reviews Section (TASK-191) */}
      {pendingReviews.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 mb-2">
                Pending Document Reviews ({pendingReviews.length})
              </h3>
              <p className="text-sm text-amber-700 mb-3">
                The following documents have been processed and are awaiting your review before being saved to the client profile.
              </p>
              <div className="space-y-2">
                {pendingReviews.map((review) => (
                  <div
                    key={review.staging_id}
                    className="bg-white rounded border border-amber-200 p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {review.filename || 'Uploaded Document'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {review.target_form_type === 'kyc_profile' ? 'KYC Profile' : review.target_form_type} •
                          Confidence: {(review.extraction_confidence * 100).toFixed(0)}% •
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleReviewDocument(review)}
                      className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded hover:bg-amber-700 transition-colors"
                    >
                      Review Extracted Data
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TASK-361: Request Documents Section */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => setUploadLinksExpanded(!uploadLinksExpanded)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-[#002B45]" />
            <span className="font-medium text-gray-900">Request Documents from Client</span>
            {uploadLinks.filter((l) => l.status === 'active').length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                {uploadLinks.filter((l) => l.status === 'active').length} active
              </span>
            )}
          </div>
          {uploadLinksExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {uploadLinksExpanded && (
          <div className="border-t border-gray-100 p-4 space-y-4">
            {/* Create new link button */}
            <Button
              onClick={() => setShowCreateLinkModal(true)}
              className="flex items-center gap-2"
            >
              <Plus size={16} />
              Generate Secure Upload Link
            </Button>

            {/* Existing links */}
            {uploadLinks.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Recent Upload Links</p>
                {uploadLinks.slice(0, 5).map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUploadLinkStatusColor(link.status)}`}>
                        {getUploadLinkStatusLabel(link.status)}
                      </span>
                      <div className="text-sm">
                        <span className="text-gray-600">
                          {link.uploads_used}/{link.max_uploads} uploads
                        </span>
                        <span className="text-gray-400 mx-2">•</span>
                        <span className="text-gray-500">
                          Expires {new Date(link.expires_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    {link.status === 'active' && link.portal_url && (
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(link.portal_url!);
                            toast.success('Link copied to clipboard');
                          } catch {
                            toast.error('Failed to copy link');
                          }
                        }}
                        className="text-sm text-[#002B45] hover:underline"
                      >
                        Copy Link
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {uploadLinks.length === 0 && (
              <p className="text-sm text-gray-500">
                No upload links created yet. Generate a secure link to request documents from this client.
              </p>
            )}
          </div>
        )}
      </div>

      {/* TASK-282: MinIO Documents Section - Documents from MinIO storage */}
      <MinIODocumentsSection
        clientId={clientId}
        onDocumentCountChange={setMinioDocumentCount}
      />

      {/* Legacy Documents Section - Documents from upload service */}
      {documents.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900">
              Processing History
            </h3>
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {documents.length}
            </span>
          </div>

          {/* Filters */}
          <DocumentFilters filters={filters} onFilterChange={setFilters} />

          {/* Document count */}
          <div className="text-sm text-gray-600">
            Showing {filteredDocuments.length} of {documents.length} documents
          </div>

          {/* Documents list */}
          {filteredDocuments.length > 0 ? (
            <div className="space-y-3">
              {filteredDocuments.map((document) => (
                <DocumentCard
                  key={document.upload_id}
                  document={document}
                  onView={handleView}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No documents match your filters</p>
            </div>
          )}
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewType === 'pdf' && previewUploadId && (
        <PDFPreviewModal
          uploadId={previewUploadId}
          isOpen={true}
          onClose={handleClosePreview}
        />
      )}

      {/* Data Preview Modal (Excel/CSV) */}
      {previewType === 'data' && previewUploadId && (
        <DataPreviewModal
          uploadId={previewUploadId}
          isOpen={true}
          onClose={handleClosePreview}
        />
      )}

      {/* Extraction Preview Modal - Unified preview for all extracted data */}
      {previewType === 'extraction' && previewUploadId && (
        <ExtractionPreviewModal
          uploadId={previewUploadId}
          stagingId={previewStagingId || undefined}
          clientId={clientId}
          isOpen={true}
          onClose={handleClosePreview}
          onApprove={() => {
            handleClosePreview();
            // Refresh both documents and pending reviews after approval
            fetchDocuments();
            fetchPendingReviews();
          }}
          // TASK-232: Navigate to compliance tab after prefill
          onApproveWithPrefill={() => {
            console.log('🚀 TASK-232: DocumentsTab navigating to Compliance tab');
            onNavigateToCompliance?.();
          }}
        />
      )}

      {/* KYC Extraction Review Modal (TASK-191) */}
      {showKYCReview && reviewingStagingId && (
        <KYCExtractionReview
          sessionId={reviewingStagingId}
          clientId={clientId}
          onApproved={handleKYCApproved}
          onClose={handleKYCReviewClose}
        />
      )}

      {/* TASK-361: Generate Upload Link Modal */}
      <GenerateUploadLinkModal
        isOpen={showCreateLinkModal}
        onClose={() => setShowCreateLinkModal(false)}
        clientId={clientId}
        clientName={clientName}
        clientEmail={clientEmail}
        onSuccess={handleLinkCreated}
      />
    </div>
  );
};

export default DocumentsTab;
